import { useState } from "react";
import { saveSavings } from "../../lib/storage";
import type { Chore } from "../../data/chores";

const EMOJIS = ["🎮","🚲","👟","🎵","📱","🎸","⚽","🏀","🎨","📚","✈️","🍕","🎬","🐶","🌟","💻","🎒","🎪","🏋️","🎯"];
const PRESETS = [
  { label: "Saver",    spending: 60, nestEgg: 40 },
  { label: "Balanced", spending: 70, nestEgg: 30 },
  { label: "Spender",  spending: 80, nestEgg: 20 },
];

function fmt(n: number) { return "$" + Math.abs(n).toFixed(2); }
function rc(n: number)  { return Math.round(n * 100) / 100; }

const EMPTY: any = {
  allocation: { spending: 70, nestEgg: 30 },
  spending: 0, nestEgg: 0,
  goals: [], transactions: [],
};

type Modal = null | "earned" | "addGoal" | "fundGoal" | "manage" | "spendGoal" | "spendBucket" | "historySpending" | "settings";

export default function KidsSavingsScreen({ data, onSave, choresData, onChoreSubmit }: { data: any; onSave: (d: any) => void; choresData: any; onChoreSubmit: (choreId: string) => void }) {
  const d = data ?? EMPTY;
  const { allocation, spending, nestEgg, goals, transactions } = d;

  const [subTab, setSubTab] = useState<"savings" | "chores">("savings");
  const [modal, setModal]               = useState<Modal>(null);
  const [manageGoalId, setManageGoalId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);

  // Earned form
  const [earnAmt, setEarnAmt]   = useState("");
  const [earnNote, setEarnNote] = useState("");

  // Add-goal form
  const [goalName, setGoalName]     = useState("");
  const [goalEmoji, setGoalEmoji]   = useState("🎮");
  const [goalTarget, setGoalTarget] = useState("");

  // Fund-goal form (spending → goal)
  const [fundAmt, setFundAmt] = useState("");

  // Spend-bucket form
  const [spendAmt, setSpendAmt]   = useState("");
  const [spendNote, setSpendNote] = useState("");

  // Spend-from-goal form
  const [spendGoalAmt, setSpendGoalAmt]   = useState("");
  const [spendGoalNote, setSpendGoalNote] = useState("");

  // Settings form (local copy while editing)
  const [settingsAlloc, setSettingsAlloc] = useState({ ...allocation });

  const activeGoals    = goals.filter((g: any) => !g.completed);
  const completedGoals = goals.filter((g: any) =>  g.completed);
  const manageGoal     = manageGoalId ? goals.find((g: any) => g.id === manageGoalId) : null;

  const nestEggProjection = rc(nestEgg * Math.pow(1.07, 10));

  function persist(next: any) { onSave(next); saveSavings(next); }
  function openManage(goalId: string) { setManageGoalId(goalId); setConfirmDelete(false); setModal("manage"); }
  function openFundGoal(goalId: string) { setManageGoalId(goalId); setFundAmt(""); setModal("fundGoal"); }
  function closeModal() { setModal(null); setManageGoalId(null); setConfirmDelete(false); }

  // ── Earn & split (spending + nest egg only) ───────────────────────────────
  function calcSplit(amount: number) {
    const spendingAmt = rc(amount * allocation.spending / 100);
    const nestEggAmt  = rc(amount - spendingAmt);
    return { spendingAmt, nestEggAmt };
  }

  function submitEarned() {
    const amount = parseFloat(earnAmt);
    if (!amount || amount <= 0) return;
    const { spendingAmt, nestEggAmt } = calcSplit(amount);
    const tx = { id: Date.now().toString(), type: "earn", amount, note: earnNote.trim() || "Money earned", date: new Date().toISOString() };
    persist({ ...d, spending: rc(spending + spendingAmt), nestEgg: rc(nestEgg + nestEggAmt), transactions: [tx, ...transactions] });
    setEarnAmt(""); setEarnNote("");
    closeModal();
  }

  // ── Fund a goal (move from spending) ─────────────────────────────────────
  function submitFundGoal() {
    const val = parseFloat(fundAmt);
    if (!val || val <= 0 || !manageGoal) return;
    const capped   = rc(Math.min(val, spending));
    const newGoals = goals.map((g: any) => g.id === manageGoal.id ? { ...g, balance: rc(g.balance + capped) } : g);
    const tx = { id: Date.now().toString(), type: "fundGoal", amount: capped, goalId: manageGoal.id, note: `Added to ${manageGoal.name}`, date: new Date().toISOString() };
    persist({ ...d, goals: newGoals, spending: rc(spending - capped), transactions: [tx, ...transactions] });
    setFundAmt("");
    closeModal();
  }

  // ── Add goal ─────────────────────────────────────────────────────────────
  function submitAddGoal() {
    const t = parseFloat(goalTarget);
    if (!goalName.trim() || !t || t <= 0) return;
    const newGoal = { id: Date.now().toString(), name: goalName.trim(), emoji: goalEmoji, target: t, balance: 0, completed: false, completedAt: null };
    persist({ ...d, goals: [...goals, newGoal] });
    setGoalName(""); setGoalTarget(""); setGoalEmoji("🎮");
    closeModal();
  }

  // ── Accomplish / delete (roll balance back to spending) ───────────────────
  function markAccomplished(goalId: string) {
    const newGoals = goals.map((x: any) => x.id === goalId ? { ...x, completed: true, completedAt: new Date().toISOString(), balance: 0 } : x);
    persist({ ...d, goals: newGoals });
    closeModal();
  }

  function deleteGoal(goalId: string) {
    const g = goals.find((x: any) => x.id === goalId);
    const rolled = g ? rc(g.balance) : 0;
    persist({ ...d, goals: goals.filter((x: any) => x.id !== goalId), transactions: transactions.filter((t: any) => t.goalId !== goalId), spending: rc(spending + rolled) });
    closeModal();
  }

  // ── Spend from spending bucket ────────────────────────────────────────────
  function submitSpendBucket() {
    const val = parseFloat(spendAmt);
    if (!val || val <= 0 || val > spending + 0.001) return;
    const tx = { id: Date.now().toString(), type: "spend", amount: -val, note: spendNote.trim() || "Spent", date: new Date().toISOString() };
    persist({ ...d, spending: rc(spending - val), transactions: [tx, ...transactions] });
    setSpendAmt(""); setSpendNote("");
    closeModal();
  }

  // ── Spend from goal (goal balance → out) ─────────────────────────────────
  function submitSpendGoal() {
    const val = parseFloat(spendGoalAmt);
    if (!val || val <= 0 || !manageGoal) return;
    const capped    = Math.min(val, manageGoal.balance);
    const newGoals  = goals.map((g: any) => g.id === manageGoal.id ? { ...g, balance: rc(g.balance - capped) } : g);
    const tx = { id: Date.now().toString(), type: "spendGoal", amount: -capped, goalId: manageGoal.id, note: spendGoalNote.trim() || `Spent on ${manageGoal.name}`, date: new Date().toISOString() };
    persist({ ...d, goals: newGoals, transactions: [tx, ...transactions] });
    setSpendGoalAmt(""); setSpendGoalNote("");
    closeModal();
  }

  // ── Allocation settings (2 buckets, auto-balance) ─────────────────────────
  function adjustAlloc(key: "spending" | "nestEgg", delta: number) {
    const other: "spending" | "nestEgg" = key === "spending" ? "nestEgg" : "spending";
    const next = { ...settingsAlloc, [key]: Math.max(key === "nestEgg" ? 10 : 20, settingsAlloc[key] + delta) };
    next[other] = 100 - next[key];
    if (next[other] < (other === "nestEgg" ? 10 : 20)) return;
    setSettingsAlloc(next);
  }
  function applyPreset(p: typeof PRESETS[0]) { setSettingsAlloc({ spending: p.spending, nestEgg: p.nestEgg }); }
  function saveSettings() { persist({ ...d, allocation: { ...settingsAlloc } }); closeModal(); }

  // ── Shared styles ─────────────────────────────────────────────────────────
  const inputStyle: any = (color = "#1f2937") => ({
    width: "100%", border: "2px solid #e5e7eb", borderRadius: 12,
    padding: "10px 14px", fontSize: 15, fontFamily: "Nunito", fontWeight: 700,
    boxSizing: "border-box", marginBottom: 12, color, outline: "none",
  });
  const bigNumInput: any = (color: string) => ({
    ...inputStyle(color), fontSize: 32, fontFamily: "Fredoka One", fontWeight: 900, textAlign: "center" as const,
  });

  const earnVal      = parseFloat(earnAmt) || 0;
  const split        = earnVal > 0 ? calcSplit(earnVal) : null;
  const spendVal     = parseFloat(spendAmt) || 0;
  const canSpend     = spendVal > 0 && spendVal <= spending + 0.001;
  const fundVal      = parseFloat(fundAmt) || 0;
  const canFund      = fundVal > 0 && fundVal <= spending + 0.001 && !!manageGoal;
  const spendGoalVal = parseFloat(spendGoalAmt) || 0;
  const canSpendGoal = spendGoalVal > 0 && !!manageGoal && spendGoalVal <= manageGoal.balance + 0.001;

  // ── Chores helpers ────────────────────────────────────────────────────────
  const chores: Chore[]  = choresData?.chores  ?? [];
  const pending: string[] = choresData?.pending ?? [];
  const history: any[]   = choresData?.history  ?? [];

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "Nunito", paddingBottom: 100 }}>

      {/* ── Header ── */}
      <div style={{ background: "linear-gradient(135deg,#10b981,#059669)", padding: "20px 18px 18px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -20, right: -10, fontSize: 100, opacity: 0.1 }}>💰</div>
        <div style={{ fontSize: 26, fontWeight: 900, color: "#fff", fontFamily: "Fredoka One", marginBottom: 14 }}>💰 My Money</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
          {[
            { label: "SPENDING",  val: spending, pct: allocation.spending, color: "#bae6fd" },
            { label: "NEST EGG",  val: nestEgg,  pct: allocation.nestEgg,  color: "#ddd6fe" },
          ].map(s => (
            <div key={s.label} style={{ background: "#ffffff20", borderRadius: 14, padding: "12px 12px 10px", textAlign: "center" }}>
              <div style={{ fontSize: 9, color: "#d1fae5", fontWeight: 800, letterSpacing: 1, marginBottom: 2 }}>{s.label}</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#fff", fontFamily: "Fredoka One", lineHeight: 1.1 }}>{fmt(s.val)}</div>
              <div style={{ fontSize: 10, color: "#a7f3d0", fontWeight: 800, marginTop: 3 }}>{s.pct}% of income</div>
            </div>
          ))}
        </div>
        <button onClick={() => { setSettingsAlloc({ ...allocation }); setModal("settings"); }}
          style={{ display: "block", margin: "0 auto", background: "#ffffff25", border: "none", borderRadius: 20, padding: "5px 18px", color: "#d1fae5", fontWeight: 800, fontSize: 12, cursor: "pointer", fontFamily: "Nunito" }}>
          ⚙️ Change my split
        </button>
      </div>

      {/* ── Sub-tab switcher ── */}
      <div style={{ display: "flex", background: "#f0fdf4", borderBottom: "2px solid #d1fae5" }}>
        {([{ id: "savings", label: "💰 Savings" }, { id: "chores", label: "🧹 Chores" }] as const).map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)}
            style={{ flex: 1, padding: "10px 0", background: subTab === t.id ? "#fff" : "none", border: "none", borderBottom: subTab === t.id ? "3px solid #10b981" : "3px solid transparent", fontWeight: 900, fontSize: 14, color: subTab === t.id ? "#059669" : "#9ca3af", cursor: "pointer", fontFamily: "Nunito", transition: "all 0.15s" }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── CHORES TAB ── */}
      {subTab === "chores" && (
        <div style={{ padding: "16px" }}>
          {chores.length === 0 ? (
            <div style={{ textAlign: "center", padding: "48px 24px" }}>
              <div style={{ fontSize: 52, marginBottom: 12 }}>🧹</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: "#1f2937", fontFamily: "Fredoka One", marginBottom: 6 }}>No chores yet!</div>
              <div style={{ fontSize: 14, color: "#6b7280", fontWeight: 700 }}>Ask a parent to set up your chore list in the Parent Portal.</div>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 700, marginBottom: 14 }}>
                Tap "Done!" when you finish a chore — your parent will approve it and you'll earn 🪙 coins!
              </div>
              {chores.map((c: Chore) => {
                const isPending = pending.includes(c.id);
                const recentApproval = history.filter((h: any) => h.choreId === c.id).slice(-1)[0];
                return (
                  <div key={c.id} style={{ background: "#fff", borderRadius: 18, marginBottom: 10, border: `2px solid ${isPending ? "#fbbf24" : "#e5e7eb"}`, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ fontSize: 36 }}>{c.emoji}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 15, fontWeight: 900, color: "#1f2937", fontFamily: "Fredoka One" }}>{c.name}</div>
                      <div style={{ fontSize: 12, fontWeight: 800, color: "#f59e0b" }}>+{c.coins} 🪙</div>
                      {recentApproval && (
                        <div style={{ fontSize: 11, color: "#10b981", fontWeight: 700, marginTop: 2 }}>
                          Last done: {new Date(recentApproval.date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    {isPending ? (
                      <div style={{ background: "#fef3c7", border: "1.5px solid #fbbf24", borderRadius: 12, padding: "6px 12px", fontSize: 12, fontWeight: 900, color: "#92400e" }}>⏳ Waiting</div>
                    ) : (
                      <button onClick={() => onChoreSubmit(c.id)}
                        style={{ background: "linear-gradient(135deg,#10b981,#059669)", border: "none", borderRadius: 12, padding: "8px 16px", color: "#fff", fontWeight: 900, fontSize: 13, cursor: "pointer", fontFamily: "Nunito" }}>
                        Done! ✓
                      </button>
                    )}
                  </div>
                );
              })}
              {history.length > 0 && (
                <div style={{ marginTop: 16, background: "#f0fdf4", borderRadius: 16, padding: "12px 14px", border: "1.5px solid #d1fae5" }}>
                  <div style={{ fontSize: 13, fontWeight: 900, color: "#059669", marginBottom: 8 }}>🏅 Recent Completions</div>
                  {history.slice(-5).reverse().map((h: any) => (
                    <div key={h.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderBottom: "1px solid #d1fae5" }}>
                      <span style={{ fontSize: 18 }}>{h.choreEmoji}</span>
                      <span style={{ flex: 1, fontSize: 13, fontWeight: 800, color: "#374151" }}>{h.choreName}</span>
                      <span style={{ fontSize: 12, fontWeight: 900, color: "#f59e0b" }}>+{h.coins} 🪙</span>
                      <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 700 }}>{new Date(h.date).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── SAVINGS TAB ── */}
      {subTab === "savings" && (
      <div style={{ padding: "16px 16px 0" }}>
        <button onClick={() => { setEarnAmt(""); setEarnNote(""); setModal("earned"); }}
          style={{ width: "100%", background: "linear-gradient(135deg,#10b981,#059669)", border: "none", borderRadius: 18, padding: "16px", color: "#fff", fontWeight: 900, fontSize: 18, cursor: "pointer", fontFamily: "Fredoka One", boxShadow: "0 4px 20px #10b98133", marginBottom: 16 }}>
          💵 I Earned Money!
        </button>

        {/* ── Savings Goals section ── */}
        <div style={{ fontSize: 16, fontWeight: 900, color: "#1f2937", fontFamily: "Fredoka One", marginBottom: 10 }}>🎯 Savings Goals</div>
        <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 700, marginBottom: 12 }}>
          Move spending money into a goal whenever you're ready.
        </div>

        {activeGoals.length === 0 ? (
          <div style={{ textAlign: "center", padding: "28px 20px", background: "#fff", borderRadius: 20, border: "2.5px dashed #6ee7b7", marginBottom: 14 }}>
            <div style={{ fontSize: 44, marginBottom: 8 }}>🐷</div>
            <div style={{ fontSize: 16, fontWeight: 900, color: "#1f2937", fontFamily: "Fredoka One" }}>No goals yet!</div>
            <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 700, marginTop: 4 }}>Add a goal and use your spending money to fund it</div>
          </div>
        ) : (
          activeGoals.map((g: any) => {
            const pct     = g.target > 0 ? Math.min(1, g.balance / g.target) : 0;
            const reached = g.target > 0 && g.balance >= g.target;
            return (
              <div key={g.id} style={{ background: "#fff", borderRadius: 20, marginBottom: 12, border: `2.5px solid ${reached ? "#10b981" : "#e5e7eb"}`, boxShadow: "0 3px 12px #0001" }}>
                <div style={{ padding: "14px 16px 10px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <div style={{ fontSize: 36 }}>{g.emoji}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 16, fontWeight: 900, color: "#1f2937", fontFamily: "Fredoka One" }}>{g.name}</div>
                      <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700 }}>Goal: {fmt(g.target)}</div>
                    </div>
                    <div style={{ textAlign: "right" as const }}>
                      <div style={{ fontSize: 20, fontWeight: 900, color: "#10b981", fontFamily: "Fredoka One" }}>{fmt(g.balance)}</div>
                      <button onClick={() => openManage(g.id)} style={{ background: "#f3f4f6", border: "none", borderRadius: 8, padding: "2px 8px", fontSize: 16, cursor: "pointer", color: "#6b7280", marginTop: 2 }}>⋯</button>
                    </div>
                  </div>
                  <div style={{ background: "#f3f4f6", borderRadius: 20, height: 12, overflow: "hidden", marginBottom: 8 }}>
                    <div style={{ width: `${pct * 100}%`, height: "100%", background: reached ? "linear-gradient(90deg,#10b981,#059669)" : "linear-gradient(90deg,#fbbf24,#f97316)", borderRadius: 20, transition: "width 0.6s" }} />
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: reached ? "#10b981" : "#f97316", marginBottom: reached ? 0 : 10 }}>
                    {reached ? "🎉 Goal reached! Tap ⋯ to mark accomplished" : `${Math.round(pct * 100)}% there — ${fmt(g.target - g.balance)} to go!`}
                  </div>
                  {!reached && (
                    <button onClick={() => openFundGoal(g.id)} disabled={spending <= 0}
                      style={{ width: "100%", background: spending > 0 ? "linear-gradient(135deg,#10b981,#059669)" : "#f3f4f6", border: "none", borderRadius: 12, padding: "9px", color: spending > 0 ? "#fff" : "#9ca3af", fontWeight: 900, fontSize: 13, cursor: spending > 0 ? "pointer" : "default", fontFamily: "Nunito" }}>
                      {spending > 0 ? "💰 Add to Goal →" : "Earn money to fund this goal"}
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}

        <button onClick={() => { setGoalName(""); setGoalTarget(""); setGoalEmoji("🎮"); setModal("addGoal"); }}
          style={{ width: "100%", background: "#fff", border: "2.5px dashed #10b981", borderRadius: 16, padding: "12px", color: "#059669", fontWeight: 900, fontSize: 15, cursor: "pointer", fontFamily: "Fredoka One", marginBottom: 20 }}>
          + Add New Goal
        </button>

        {/* ── Spending bucket section ── */}
        <div style={{ background: "#fff", borderRadius: 20, border: "2.5px solid #bae6fd", marginBottom: 14, overflow: "hidden" }}>
          <div style={{ background: "linear-gradient(135deg,#0ea5e9,#0284c7)", padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 900, color: "#fff", fontFamily: "Fredoka One" }}>💸 Spending Money</div>
              <div style={{ fontSize: 12, color: "#bae6fd", fontWeight: 700 }}>Free to use however you want</div>
            </div>
            <div style={{ fontSize: 24, fontWeight: 900, color: "#fff", fontFamily: "Fredoka One" }}>{fmt(spending)}</div>
          </div>
          <div style={{ padding: "12px 16px", display: "flex", gap: 8 }}>
            <button onClick={() => { setSpendAmt(""); setSpendNote(""); setModal("spendBucket"); }} disabled={spending <= 0}
              style={{ flex: 1, background: spending > 0 ? "linear-gradient(135deg,#0ea5e9,#0284c7)" : "#f3f4f6", border: "none", borderRadius: 12, padding: "9px", color: spending > 0 ? "#fff" : "#9ca3af", fontWeight: 900, fontSize: 13, cursor: spending > 0 ? "pointer" : "default", fontFamily: "Nunito" }}>
              💸 Record a Spend
            </button>
            <button onClick={() => setModal("historySpending")}
              style={{ background: "#f3f4f6", border: "none", borderRadius: 12, padding: "9px 14px", color: "#6b7280", fontWeight: 900, fontSize: 14, cursor: "pointer" }}>📜</button>
          </div>
        </div>

        {/* ── Nest Egg section ── */}
        <div style={{ background: "linear-gradient(135deg,#8b5cf6,#7c3aed)", borderRadius: 20, padding: "16px", marginBottom: 20, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: -10, right: -10, fontSize: 80, opacity: 0.1 }}>🥚</div>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 900, color: "#fff", fontFamily: "Fredoka One" }}>🥚 Nest Egg</div>
              <div style={{ fontSize: 12, color: "#ddd6fe", fontWeight: 700 }}>🔒 Locked — your future self says thanks</div>
            </div>
            <div style={{ textAlign: "right" as const }}>
              <div style={{ fontSize: 26, fontWeight: 900, color: "#fff", fontFamily: "Fredoka One" }}>{fmt(nestEgg)}</div>
            </div>
          </div>
          {nestEgg > 0 && (
            <div style={{ background: "#ffffff20", borderRadius: 12, padding: "8px 12px" }}>
              <div style={{ fontSize: 12, color: "#ede9fe", fontWeight: 800 }}>
                📈 At 7% growth, this could be <strong style={{ color: "#fff" }}>{fmt(nestEggProjection)}</strong> in 10 years!
              </div>
            </div>
          )}
        </div>

        {/* ── Accomplished Goals ── */}
        {completedGoals.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <button onClick={() => setShowCompleted(v => !v)}
              style={{ width: "100%", background: "#fff", border: "2.5px solid #d1fae5", borderRadius: 16, padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", marginBottom: showCompleted ? 12 : 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 18 }}>🏆</span>
                <span style={{ fontSize: 14, fontWeight: 900, color: "#059669", fontFamily: "Fredoka One" }}>Accomplished Goals</span>
                <span style={{ background: "#d1fae5", color: "#059669", fontSize: 11, fontWeight: 900, borderRadius: 20, padding: "2px 8px" }}>{completedGoals.length}</span>
              </div>
              <span style={{ fontSize: 16, color: "#9ca3af" }}>{showCompleted ? "▲" : "▼"}</span>
            </button>
            {showCompleted && completedGoals.map((g: any) => (
              <div key={g.id} style={{ background: "#f9fafb", borderRadius: 18, marginBottom: 10, border: "2px solid #d1fae5", opacity: 0.85 }}>
                <div style={{ padding: "12px 14px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ fontSize: 32 }}>{g.emoji}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 15, fontWeight: 900, color: "#6b7280", fontFamily: "Fredoka One", display: "flex", alignItems: "center", gap: 6 }}>
                        {g.name} <span>🏆</span>
                      </div>
                      <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 700 }}>
                        Goal: {fmt(g.target)}{g.completedAt ? ` · Done ${new Date(g.completedAt).toLocaleDateString()}` : ""}
                      </div>
                    </div>
                    <button onClick={() => openManage(g.id)} style={{ background: "#e5e7eb", border: "none", borderRadius: 8, padding: "2px 8px", fontSize: 14, cursor: "pointer", color: "#6b7280" }}>⋯</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      )} {/* end savings tab */}

      {/* ══ MODALS ══════════════════════════════════════════════════════════ */}
      {modal && (
        <div style={{ position: "fixed", inset: 0, background: "#00000066", zIndex: 100, display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={closeModal}>
          <div style={{ background: "#fff", borderRadius: "24px 24px 0 0", padding: "24px 20px 44px", width: "100%", maxWidth: 480, maxHeight: "92vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            <div style={{ width: 40, height: 4, background: "#e5e7eb", borderRadius: 4, margin: "0 auto 20px" }} />

            {/* ── I Earned Money ── */}
            {modal === "earned" && <>
              <div style={{ fontSize: 22, fontWeight: 900, fontFamily: "Fredoka One", color: "#1f2937", marginBottom: 16 }}>💵 I Earned Money!</div>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#374151", marginBottom: 6 }}>How much did you earn?</div>
              <input type="number" value={earnAmt} onChange={e => setEarnAmt(e.target.value)} placeholder="0.00" style={bigNumInput("#10b981")} autoFocus />
              <div style={{ fontSize: 13, fontWeight: 800, color: "#374151", marginBottom: 6 }}>Where did it come from? (optional)</div>
              <input value={earnNote} onChange={e => setEarnNote(e.target.value)} placeholder="Allowance, birthday, chores..." style={inputStyle()} />
              {split && (
                <div style={{ background: "#f0fdf4", border: "2px solid #bbf7d0", borderRadius: 14, padding: "14px 16px", marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 900, color: "#166534", marginBottom: 10 }}>Here's how it splits:</div>
                  {[
                    { emoji: "💸", label: "Spending",  amt: split.spendingAmt, pct: allocation.spending, color: "#0284c7" },
                    { emoji: "🥚", label: "Nest Egg",  amt: split.nestEggAmt,  pct: allocation.nestEgg,  color: "#7c3aed" },
                  ].map(row => (
                    <div key={row.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 18 }}>{row.emoji}</span>
                        <span style={{ fontSize: 13, fontWeight: 800, color: "#374151" }}>{row.label}</span>
                        <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 700 }}>{row.pct}%</span>
                      </div>
                      <span style={{ fontSize: 16, fontWeight: 900, color: row.color, fontFamily: "Fredoka One" }}>{fmt(row.amt)}</span>
                    </div>
                  ))}
                  <div style={{ marginTop: 10, padding: "8px 12px", background: "#dcfce7", borderRadius: 10, fontSize: 12, fontWeight: 800, color: "#166534" }}>
                    💡 Move spending money to your goals whenever you want!
                  </div>
                </div>
              )}
              <button onClick={submitEarned} disabled={!earnVal}
                style={{ width: "100%", background: earnVal > 0 ? "linear-gradient(135deg,#10b981,#059669)" : "#f3f4f6", border: "none", borderRadius: 14, padding: "14px", color: earnVal > 0 ? "#fff" : "#9ca3af", fontWeight: 900, fontSize: 16, cursor: earnVal > 0 ? "pointer" : "default", fontFamily: "Fredoka One" }}>
                Confirm & Split! 🎉
              </button>
            </>}

            {/* ── Add Goal ── */}
            {modal === "addGoal" && <>
              <div style={{ fontSize: 22, fontWeight: 900, fontFamily: "Fredoka One", color: "#1f2937", marginBottom: 16 }}>🎯 New Savings Goal</div>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#374151", marginBottom: 6 }}>What are you saving for?</div>
              <input value={goalName} onChange={e => setGoalName(e.target.value)} placeholder="e.g. New bike, video game..." style={inputStyle()} />
              <div style={{ fontSize: 13, fontWeight: 800, color: "#374151", marginBottom: 8 }}>Pick an emoji</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 14 }}>
                {EMOJIS.map(e => (
                  <button key={e} onClick={() => setGoalEmoji(e)} style={{ fontSize: 22, background: goalEmoji === e ? "#dcfce7" : "#f9fafb", border: `2px solid ${goalEmoji === e ? "#10b981" : "#e5e7eb"}`, borderRadius: 10, padding: "4px 6px", cursor: "pointer" }}>{e}</button>
                ))}
              </div>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#374151", marginBottom: 6 }}>Savings target ($)</div>
              <input type="number" value={goalTarget} onChange={e => setGoalTarget(e.target.value)} placeholder="e.g. 120.00" style={inputStyle()} />
              <button onClick={submitAddGoal} style={{ width: "100%", background: "linear-gradient(135deg,#10b981,#059669)", border: "none", borderRadius: 14, padding: "14px", color: "#fff", fontWeight: 900, fontSize: 16, cursor: "pointer", fontFamily: "Fredoka One" }}>Create Goal! 🎯</button>
            </>}

            {/* ── Fund Goal (move from spending) ── */}
            {modal === "fundGoal" && manageGoal && <>
              <div style={{ fontSize: 22, fontWeight: 900, fontFamily: "Fredoka One", color: "#1f2937", marginBottom: 4 }}>
                {manageGoal.emoji} Add to {manageGoal.name}
              </div>
              <div style={{ fontSize: 14, color: "#6b7280", fontWeight: 700, marginBottom: 16 }}>
                Your spending: <strong style={{ color: "#0284c7" }}>{fmt(spending)}</strong>
              </div>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#374151", marginBottom: 6 }}>How much to move?</div>
              <input type="number" value={fundAmt} onChange={e => setFundAmt(e.target.value)} placeholder="0.00" style={bigNumInput("#10b981")} autoFocus />
              {fundVal > spending && (
                <div style={{ color: "#ef4444", fontSize: 12, fontWeight: 800, marginBottom: 8, marginTop: -8 }}>
                  Max you can move is {fmt(spending)}
                </div>
              )}
              <button onClick={submitFundGoal} disabled={!canFund}
                style={{ width: "100%", background: canFund ? "linear-gradient(135deg,#10b981,#059669)" : "#f3f4f6", border: "none", borderRadius: 14, padding: "14px", color: canFund ? "#fff" : "#9ca3af", fontWeight: 900, fontSize: 16, cursor: canFund ? "pointer" : "default", fontFamily: "Fredoka One" }}>
                Move to Goal 💰
              </button>
            </>}

            {/* ── Manage (⋯) ── */}
            {modal === "manage" && manageGoal && <>
              <div style={{ fontSize: 20, fontWeight: 900, fontFamily: "Fredoka One", color: "#1f2937", marginBottom: 4 }}>{manageGoal.emoji} {manageGoal.name}</div>
              <div style={{ fontSize: 13, color: "#9ca3af", fontWeight: 700, marginBottom: 20 }}>
                {manageGoal.completed ? `Accomplished ${new Date(manageGoal.completedAt).toLocaleDateString()}` : `Saved: ${fmt(manageGoal.balance)} · Goal: ${fmt(manageGoal.target)}`}
              </div>
              {!confirmDelete ? <>
                {!manageGoal.completed && (
                  <>
                    <button onClick={() => markAccomplished(manageGoal.id)}
                      style={{ width: "100%", background: "linear-gradient(135deg,#10b981,#059669)", border: "none", borderRadius: 14, padding: "14px", color: "#fff", fontWeight: 900, fontSize: 15, cursor: "pointer", fontFamily: "Fredoka One", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                      <span>🏆</span> Mark as Accomplished
                    </button>
                    {manageGoal.balance > 0 && (
                      <button onClick={() => { setSpendGoalAmt(""); setSpendGoalNote(""); setModal("spendGoal"); }}
                        style={{ width: "100%", background: "linear-gradient(135deg,#f97316,#ea580c)", border: "none", borderRadius: 14, padding: "14px", color: "#fff", fontWeight: 900, fontSize: 15, cursor: "pointer", fontFamily: "Fredoka One", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                        <span>💸</span> I Spent on This Goal
                      </button>
                    )}
                  </>
                )}
                <button onClick={() => setConfirmDelete(true)}
                  style={{ width: "100%", background: "#fff", border: "2px solid #fee2e2", borderRadius: 14, padding: "14px", color: "#ef4444", fontWeight: 900, fontSize: 15, cursor: "pointer", fontFamily: "Fredoka One", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                  <span>🗑️</span> Delete Goal
                </button>
              </> : <>
                <div style={{ background: "#fee2e2", borderRadius: 14, padding: "14px 16px", marginBottom: 16, textAlign: "center" as const }}>
                  <div style={{ fontSize: 28, marginBottom: 6 }}>⚠️</div>
                  <div style={{ fontSize: 15, fontWeight: 900, color: "#991b1b" }}>Delete this goal?</div>
                  <div style={{ fontSize: 13, color: "#b91c1c", fontWeight: 700, marginTop: 4 }}>Any saved balance will return to your spending money.</div>
                </div>
                <button onClick={() => deleteGoal(manageGoal.id)} style={{ width: "100%", background: "linear-gradient(135deg,#ef4444,#dc2626)", border: "none", borderRadius: 14, padding: "14px", color: "#fff", fontWeight: 900, fontSize: 15, cursor: "pointer", fontFamily: "Fredoka One", marginBottom: 10 }}>Yes, Delete It</button>
                <button onClick={() => setConfirmDelete(false)} style={{ width: "100%", background: "#f3f4f6", border: "none", borderRadius: 14, padding: "14px", color: "#374151", fontWeight: 900, fontSize: 15, cursor: "pointer", fontFamily: "Fredoka One" }}>Cancel</button>
              </>}
            </>}

            {/* ── Spend from bucket ── */}
            {modal === "spendBucket" && <>
              <div style={{ fontSize: 22, fontWeight: 900, fontFamily: "Fredoka One", color: "#1f2937", marginBottom: 4 }}>💸 Record a Spend</div>
              <div style={{ fontSize: 14, color: "#6b7280", fontWeight: 700, marginBottom: 16 }}>Available: <strong style={{ color: "#0284c7" }}>{fmt(spending)}</strong></div>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#374151", marginBottom: 6 }}>How much?</div>
              <input type="number" value={spendAmt} onChange={e => setSpendAmt(e.target.value)} placeholder="0.00" style={bigNumInput("#0284c7")} />
              <div style={{ fontSize: 13, fontWeight: 800, color: "#374151", marginBottom: 6 }}>What did you spend it on? (optional)</div>
              <input value={spendNote} onChange={e => setSpendNote(e.target.value)} placeholder="Movie, snacks, item name..." style={inputStyle()} />
              <button onClick={submitSpendBucket} disabled={!canSpend}
                style={{ width: "100%", background: canSpend ? "linear-gradient(135deg,#0ea5e9,#0284c7)" : "#f3f4f6", border: "none", borderRadius: 14, padding: "14px", color: canSpend ? "#fff" : "#9ca3af", fontWeight: 900, fontSize: 16, cursor: canSpend ? "pointer" : "default", fontFamily: "Fredoka One" }}>
                Confirm Spend 💸
              </button>
            </>}

            {/* ── Spend from goal ── */}
            {modal === "spendGoal" && manageGoal && <>
              <div style={{ fontSize: 22, fontWeight: 900, fontFamily: "Fredoka One", color: "#1f2937", marginBottom: 4 }}>
                {manageGoal.emoji} Spent on {manageGoal.name}
              </div>
              <div style={{ fontSize: 14, color: "#6b7280", fontWeight: 700, marginBottom: 16 }}>
                Available: <strong style={{ color: "#f97316" }}>{fmt(manageGoal.balance)}</strong>
              </div>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#374151", marginBottom: 6 }}>How much did you spend?</div>
              <input type="number" value={spendGoalAmt} onChange={e => setSpendGoalAmt(e.target.value)} placeholder="0.00" style={bigNumInput("#f97316")} autoFocus />
              <div style={{ fontSize: 13, fontWeight: 800, color: "#374151", marginBottom: 6 }}>What did you buy? (optional)</div>
              <input value={spendGoalNote} onChange={e => setSpendGoalNote(e.target.value)} placeholder="What did you get?" style={inputStyle()} />
              <button onClick={submitSpendGoal} disabled={!canSpendGoal}
                style={{ width: "100%", background: canSpendGoal ? "linear-gradient(135deg,#f97316,#ea580c)" : "#f3f4f6", border: "none", borderRadius: 14, padding: "14px", color: canSpendGoal ? "#fff" : "#9ca3af", fontWeight: 900, fontSize: 16, cursor: canSpendGoal ? "pointer" : "default", fontFamily: "Fredoka One" }}>
                Record Purchase 💸
              </button>
            </>}

            {/* ── History (spending bucket) ── */}
            {modal === "historySpending" && <>
              <div style={{ fontSize: 22, fontWeight: 900, fontFamily: "Fredoka One", color: "#1f2937", marginBottom: 16 }}>📜 Money History</div>
              {transactions.filter((t: any) => ["earn","spend","fundGoal"].includes(t.type)).length === 0
                ? <div style={{ textAlign: "center", padding: "24px", color: "#9ca3af", fontWeight: 700 }}>No transactions yet</div>
                : transactions.filter((t: any) => ["earn","spend","fundGoal"].includes(t.type)).map((t: any) => (
                  <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                    <div style={{ fontSize: 20 }}>{t.type === "earn" ? "💚" : t.type === "fundGoal" ? "🎯" : "🔴"}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: "#1f2937" }}>{t.note}</div>
                      <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 700 }}>{new Date(t.date).toLocaleDateString()}</div>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 900, color: t.type === "earn" ? "#10b981" : t.type === "fundGoal" ? "#059669" : "#ef4444", fontFamily: "Fredoka One" }}>
                      {t.type === "earn" ? "+" : "-"}{fmt(Math.abs(t.amount))}
                    </div>
                  </div>
                ))
              }
            </>}

            {/* ── Settings ── */}
            {modal === "settings" && <>
              <div style={{ fontSize: 22, fontWeight: 900, fontFamily: "Fredoka One", color: "#1f2937", marginBottom: 4 }}>⚙️ How I Split My Money</div>
              <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 700, marginBottom: 16 }}>Every dollar you earn splits between these two buckets.</div>

              {/* Quick presets */}
              <div style={{ fontSize: 13, fontWeight: 800, color: "#374151", marginBottom: 8 }}>Quick presets</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                {PRESETS.map(p => (
                  <button key={p.label} onClick={() => applyPreset(p)}
                    style={{ flex: 1, background: settingsAlloc.spending === p.spending ? "#dcfce7" : "#f9fafb", border: `2px solid ${settingsAlloc.spending === p.spending ? "#10b981" : "#e5e7eb"}`, borderRadius: 12, padding: "8px 4px", cursor: "pointer", fontFamily: "Nunito" }}>
                    <div style={{ fontSize: 12, fontWeight: 900, color: "#1f2937" }}>{p.label}</div>
                    <div style={{ fontSize: 10, color: "#6b7280", fontWeight: 700 }}>{p.spending}/{p.nestEgg}</div>
                  </button>
                ))}
              </div>

              {/* Sliders — auto-balance */}
              {([
                { key: "spending" as const, emoji: "💸", label: "Spending", color: "#0284c7", min: 20 },
                { key: "nestEgg"  as const, emoji: "🥚", label: "Nest Egg", color: "#7c3aed", min: 10 },
              ]).map(row => (
                <div key={row.key} style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: "#1f2937" }}>{row.emoji} {row.label} (min {row.min}%)</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <button onClick={() => adjustAlloc(row.key, -5)} style={{ background: "#f3f4f6", border: "none", borderRadius: 8, width: 28, height: 28, cursor: "pointer", fontWeight: 900, fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>−</button>
                      <span style={{ fontSize: 18, fontWeight: 900, color: row.color, fontFamily: "Fredoka One", minWidth: 40, textAlign: "center" as const }}>{settingsAlloc[row.key]}%</span>
                      <button onClick={() => adjustAlloc(row.key, 5)} style={{ background: "#f3f4f6", border: "none", borderRadius: 8, width: 28, height: 28, cursor: "pointer", fontWeight: 900, fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>+</button>
                    </div>
                  </div>
                  <div style={{ background: "#f3f4f6", borderRadius: 20, height: 8, overflow: "hidden" }}>
                    <div style={{ width: `${settingsAlloc[row.key]}%`, height: "100%", background: row.color, borderRadius: 20, transition: "width 0.2s" }} />
                  </div>
                </div>
              ))}

              <div style={{ textAlign: "center" as const, marginBottom: 16, fontSize: 13, fontWeight: 800, color: "#10b981" }}>
                Total: {settingsAlloc.spending + settingsAlloc.nestEgg}% ✓
              </div>
              <button onClick={saveSettings}
                style={{ width: "100%", background: "linear-gradient(135deg,#10b981,#059669)", border: "none", borderRadius: 14, padding: "14px", color: "#fff", fontWeight: 900, fontSize: 16, cursor: "pointer", fontFamily: "Fredoka One" }}>
                Save Split ✓
              </button>
            </>}

          </div>
        </div>
      )}
    </div>
  );
}
